import { generateUUID } from '../utils';
import { UploadRejectedError } from './types';
/** Mint a new row with a stable id — never derived from name/size/index. */ export function createPendingRow(file, overrides = {}) {
    return {
        id: generateUUID(),
        file,
        progress: 0,
        status: 'pending',
        contentType: file.type,
        ...overrides
    };
}
/** Replace exactly one row, addressed by id, leaving every other row's object identity untouched. */ export function replaceRow(rows, id, updater) {
    return rows.map((row)=>row.id === id ? updater(row) : row);
}
/** Replace exactly one row, addressed by id, with zero or more rows in its place — same
 *  position, archive order preserved. Used to swap an expanded archive row for its children. */ export function spliceRow(rows, id, replacements) {
    const index = rows.findIndex((row)=>row.id === id);
    if (index === -1) return rows;
    return [
        ...rows.slice(0, index),
        ...replacements,
        ...rows.slice(index + 1)
    ];
}
const ZIP_MIME_TYPES = new Set([
    'application/zip',
    'application/x-zip-compressed'
]);
/** Mirrors the server's ziputil.IsZip / IsZipFilename rule — mime type OR a .zip name. */ export function isZipFile(file) {
    return ZIP_MIME_TYPES.has(file.type) || file.name.toLowerCase().endsWith('.zip');
}
export function withUploading(row) {
    return {
        ...row,
        status: 'uploading',
        progress: 0
    };
}
export function withProgress(row, progress) {
    return {
        ...row,
        progress
    };
}
export function withUploaded(row, result) {
    return {
        ...row,
        status: 'uploaded',
        contentType: result.mimeType,
        jobId: result.jobId ?? null,
        attachmentId: result.artifactId,
        processingStatus: result.jobId ? 'processing' : 'ready',
        progress: 100
    };
}
export function withUploadError(row, err) {
    return {
        ...row,
        status: 'error',
        rejection: err instanceof UploadRejectedError ? err.message : undefined
    };
}
export function withProcessingStatus(row, processingStatus) {
    return {
        ...row,
        processingStatus
    };
}
/** An unpacked zip member whose conversion ran and failed (a schedule, a
 *  photo chat cannot read). It is uploaded and can still be filed, so it never
 *  blocks send. A member whose processing never started stays retryable. */ export function isUnreadableArchiveChild(row) {
    return Boolean(row.sourceArchiveName) && Boolean(row.jobId) && row.status === 'uploaded' && row.processingStatus === 'error';
}
/** One attachment per zip the batch came from, so the zip's skip reasons
 *  (blocked types, nested archives) reach the filing plan. */ export function archiveAttachments(rows) {
    const seen = new Set();
    const out = [];
    for (const row of rows){
        const id = row.sourceArchiveId;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({
            id,
            name: row.sourceArchiveName || 'archive.zip',
            contentType: 'application/zip'
        });
    }
    return out;
}
